import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "antd";
import Error404 from "MainPages/ErrorPages/error404";
import { toast } from "core/Operations/toast";
import HourlyPayslip from "./Template/HourlyPayslip";
import LocalPayslip from "./Template/LocalPayslip";
import TajmahalPayslip from "./Template/Company/Tajmahal_Payslip";
import Viva_Payslip from "./Template/Company/Viva_Payslip";
import { buildPayslipPdf, getPayslipFileName } from "./payslipPdf";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { format } from "date-fns";

const TAJMAHAL_CODES = ["TM", "TMP"];
const VIVA_CODES = ["VV", "VC"]; // "WA"

const PayslipTemplate = ({ payslipDetails, companyDetails }) => {
  const props = { payrollDetails: payslipDetails, companyDetails };
  const shortCode = companyDetails?.shortCode;

  if (TAJMAHAL_CODES.includes(shortCode)) return <TajmahalPayslip {...props} />;
  if (VIVA_CODES.includes(shortCode)) return <Viva_Payslip {...props} />;

  if (payslipDetails?.employeeType == null) return <Error404 />;

  if (payslipDetails?.salaryType === "Hourly")
    return <HourlyPayslip {...props} />;
  if (payslipDetails?.salaryType === "Monthly")
    return <LocalPayslip {...props} />;

  return null;
};

PayslipTemplate.propTypes = {
  payslipDetails: PropTypes.object,
  companyDetails: PropTypes.object,
};

const Payslip = ({
  payslipDetails,
  companyDetails,
  showDownload = true,
  pdfMode = "table",
}) => {
  const pdfRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const pdfDownloader = async () => {
    if (TAJMAHAL_CODES.includes(companyDetails?.shortCode) || VIVA_CODES.includes(companyDetails?.shortCode)) {
      setIsDownloading(true);
      try {
        const doc = await buildPayslipPdf(pdfRef.current, pdfMode);
        doc.save(getPayslipFileName(payslipDetails));
      } catch (err) {
        console.error(err);
        toast.error("Failed to download payslip");
      } finally {
        setIsDownloading(false);
      }
    } else {
      setTimeout(() => {
        const input = pdfRef.current;
        html2canvas(input, { scale: 2, useCORS: true, logging: true }).then(
          (canvas) => {
            const imgData = canvas.toDataURL("image/png", 1.0);
            const pdf = jsPDF("p", "mm", "a4", true);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;
            const imgY = 30;
            pdf.addImage(
              imgData,
              "PNG",
              imgX,
              imgY,
              imgWidth * ratio,
              imgHeight * ratio,
              undefined,
              "SLOW",
            );
            const paySlipName = `${payslipDetails?.fullName} Payslip -${format(
              new Date(payslipDetails?.toDate),
              "MMM",
            )}-${format(new Date(payslipDetails?.toDate), "yyyy")}.pdf`;
            pdf.save(paySlipName);
          },
        );
      }, 1000);
    }
  };

  return (
    <>
      {showDownload && (
        <div className="d-grid gap-2 d-md-flex justify-content-md-end mb-2">
          <Button
            type="primary"
            onClick={pdfDownloader}
            loading={isDownloading}
          >
            Download
          </Button>
        </div>
      )}

      <div ref={pdfRef} style={{ background: "#fff" }}>
        <PayslipTemplate
          payslipDetails={payslipDetails}
          companyDetails={companyDetails}
        />
      </div>
    </>
  );
};

Payslip.propTypes = {
  payslipDetails: PropTypes.object.isRequired,
  companyDetails: PropTypes.object.isRequired,
  showDownload: PropTypes.bool,
  pdfMode: PropTypes.oneOf(["table", "image"]),
};

export default Payslip;
